import {
  SUMMARY_ENTITY_LABELS,
} from 'constants/uiConstants';
import type { GUnit } from 'interfaces/IGlobalCatalogue';
import type { IInput } from 'interfaces/IInput';
import type { InputTaskCategory } from 'interfaces/IInputTask';
import type { SummaryEntityCategory } from '../../../../redux/slices/InsightsSlice';
import {
  ANALYSIS_COST_CATEGORIES,
  STEP_TYPE_TO_SUMMARY_ENTITY,
} from './computeAnalysis';
import type { IInputStepRateCost } from 'interfaces/IInputStep';
import { COST_STEP_TYPES } from './costCategories';
import { computeGrossMargin } from './computeGrossMargin';
import { resolveAreaHa, resolveEffectiveRate } from './insightUtils';

export interface CategorySpendLine {
  category: SummaryEntityCategory;
  /** Display label from SUMMARY_ENTITY_LABELS. */
  label: string;
  /** Total spend in $ (positive). */
  totalSpend: number;
  /** Area-weighted cost per ha across all steps of this category (positive). */
  ratePerHa: number;
}

export interface CategorySpendResult {
  /** Rows in ANALYSIS_COST_CATEGORIES display order; only categories with spend > 0. */
  lines: CategorySpendLine[];
  /** False when no inputs have any cost data. */
  hasData: boolean;
}

/**
 * Aggregate per-category spend across all scoped inputs.
 *
 * ── Aggregation ───────────────────────────────────────────────────────────────
 * Calls computeGrossMargin for each input, restricted to `categories` (e.g.
 * Plan only, Actual only, or both — the Live Panel calls this once per
 * category so it can show Plan/Actual side by side without conflating them).
 * Maps each CostLine's stepType → SummaryEntityCategory via
 * STEP_TYPE_TO_SUMMARY_ENTITY (CropProtection + Adjuvant → Plant Protection).
 *
 * ── Rate ($/ha) ───────────────────────────────────────────────────────────────
 * Each CostLine carries totalCost (positive $) and costPerHa ($/ha).
 * Back-calculates totalArea = totalCost / costPerHa and accumulates both,
 * then divides at the end: ratePerHa = totalSpend / totalArea.
 *
 * Pure function — no side effects, no hooks, no Redux.
 */
export const computeCategorySpend = (
  inputs: IInput[],
  gUnits: GUnit[],
  categories: Set<InputTaskCategory>
): CategorySpendResult => {
  const acc: Partial<
    Record<SummaryEntityCategory, { cost: number; area: number }>
  > = {};

  for (const input of inputs) {
    const result = computeGrossMargin(input, categories, gUnits);
    if (!result.hasData) continue;

    for (const line of result.costBreakdown) {
      const cat = STEP_TYPE_TO_SUMMARY_ENTITY[line.stepType];
      if (!cat) continue;

      const existing = acc[cat] ?? { cost: 0, area: 0 };
      // Back-calculate the area this cost line covers
      const area = line.costPerHa > 0 ? line.totalCost / line.costPerHa : 0;
      acc[cat] = {
        cost: existing.cost + line.totalCost,
        area: existing.area + area,
      };
    }
  }

  const lines: CategorySpendLine[] = [];
  for (const cat of ANALYSIS_COST_CATEGORIES) {
    const entry = acc[cat];
    if (!entry || entry.cost === 0) continue;
    lines.push({
      category: cat,
      label: SUMMARY_ENTITY_LABELS[cat] ?? cat,
      totalSpend: entry.cost,
      ratePerHa: entry.area > 0 ? entry.cost / entry.area : 0,
    });
  }

  return {
    lines,
    hasData: lines.length > 0,
  };
};

export interface ProductSpendLine {
  category: SummaryEntityCategory;
  /** Grouping key (globalId when available, otherwise item name), as in Quantity required. */
  key: string;
  label: string;
  /** Total spend in $ (positive). */
  totalSpend: number;
  /** Area-weighted cost per ha across this product's steps (positive). */
  ratePerHa: number;
}

/**
 * Per-product spend across the scoped inputs, for Category spend's product
 * rows (OP-2535). Each step costs what computeGrossMargin charges it
 * (cost × effective rate × area), so a category's products add up to its
 * computeCategorySpend total. Seed dress counts as Seed.
 *
 * Rows are in ANALYSIS_COST_CATEGORIES order, then by spend, largest first.
 *
 * Pure function — no side effects, no hooks, no Redux.
 */
export const computeProductSpend = (
  inputs: IInput[],
  gUnits: GUnit[],
  categories: Set<InputTaskCategory>
): ProductSpendLine[] => {
  const acc = new Map<
    string,
    {
      category: SummaryEntityCategory;
      label: string;
      cost: number;
      area: number;
    }
  >();

  for (const input of inputs) {
    for (const task of input.tasks) {
      if (!categories.has(task.category)) continue;
      for (const step of task.steps) {
        if (!COST_STEP_TYPES.has(step.stepType)) continue;
        const category = STEP_TYPE_TO_SUMMARY_ENTITY[step.stepType];
        if (!category) continue;

        const cost = (step as IInputStepRateCost).cost ?? 0;
        const effectiveRate = resolveEffectiveRate(step, task, gUnits);
        if (cost <= 0 || effectiveRate === null || effectiveRate <= 0) continue;
        const areaHa = resolveAreaHa(step, task, input, gUnits);
        if (areaHa <= 0) continue;

        // A step with no product name still counts, under its category's
        // name, so the rows always add up to the category total.
        const label = step.item || (SUMMARY_ENTITY_LABELS[category] ?? category);
        const key = step.globalId || label;
        // Keyed per category too, so one product never spans two categories.
        const accKey = `${category}:${key}`;
        const existing = acc.get(accKey);
        const stepCost = cost * effectiveRate * areaHa;
        if (existing) {
          existing.cost += stepCost;
          existing.area += areaHa;
        } else {
          acc.set(accKey, {
            category,
            label,
            cost: stepCost,
            area: areaHa,
          });
        }
      }
    }
  }

  const categoryRank = new Map<SummaryEntityCategory, number>(
    ANALYSIS_COST_CATEGORIES.map((category, i) => [category, i])
  );
  return Array.from(acc.entries())
    .map(([accKey, entry]) => ({
      category: entry.category,
      key: accKey.slice(entry.category.length + 1),
      label: entry.label,
      totalSpend: entry.cost,
      ratePerHa: entry.area > 0 ? entry.cost / entry.area : 0,
    }))
    .sort(
      (a, b) =>
        (categoryRank.get(a.category) ?? 0) -
          (categoryRank.get(b.category) ?? 0) || b.totalSpend - a.totalSpend
    );
};
